import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarCheck2 } from "lucide-react";

export default function ConsultationSection() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", role: "employer", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads/consultation", form);
      setDone(true);
      toast.success("Consultation requested — we'll confirm within 24h.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="consultation"
      data-testid="consultation-section"
      className="py-20 lg:py-28 bg-slate-900 text-white relative overflow-hidden"
    >
      <div aria-hidden="true" className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-blue-600/20 blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center relative">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-blue-300 font-semibold">
            Book a consultation
          </div>
          <h2 className="font-display font-bold mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            30 minutes. Zero commitment.
          </h2>
          <p className="mt-4 text-slate-300 max-w-xl">
            Tell us your hiring plan or career goals. A senior EuroAsia
            consultant will reply within 24 hours with a tailored plan, salary
            insights and a sample shortlist.
          </p>
          <ul className="mt-8 space-y-3 text-slate-300">
            {[
              "Senior recruiter, not a sales rep",
              "GDPR-compliant, EU-hosted data",
              "Available in EN / DE / NL / RO / PL",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <CalendarCheck2 className="w-5 h-5 text-blue-400 mt-0.5" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {done ? (
          <div
            data-testid="consultation-success"
            className="rounded-2xl border border-blue-400/30 bg-white/5 backdrop-blur p-8"
          >
            <div className="font-display font-bold text-2xl">
              Thanks, {form.name.split(" ")[0] || "there"}.
            </div>
            <p className="mt-2 text-slate-300">
              Your request is in. Expect a calendar invite at {form.email}{" "}
              within 24 hours.
            </p>
          </div>
        ) : (
          <form
            data-testid="consultation-form"
            onSubmit={submit}
            className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 sm:p-8 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name">
                <Input data-testid="cs-name" required value={form.name}
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Email">
                <Input data-testid="cs-email" type="email" required value={form.email}
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Field>
              <Field label="Company (optional)">
                <Input data-testid="cs-company" value={form.company}
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                  onChange={(e) => setForm({ ...form, company: e.target.value })} />
              </Field>
              <Field label="I am a…">
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="cs-role" className="bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employer">Employer / hiring</SelectItem>
                    <SelectItem value="candidate">Candidate / job seeker</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Message">
              <Textarea data-testid="cs-message" rows={3} value={form.message}
                className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                placeholder="Briefly tell us about your hiring need or goals…"
                onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </Field>
            <Button
              data-testid="cs-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white h-11"
            >
              {loading ? "Sending…" : "Request consultation"}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}
